import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, CalendarClock, Users, LogOut, Compass, Download, Building2, Shield, BarChart3, Trophy, Sparkles, Bell, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { useMode, toggleMode } from "@/lib/appearance";
import { FoundingBadge } from "@/components/FoundingBadge";
import { InnovatorBadge } from "@/components/InnovatorBadge";
import AskJeeoModal from "@/components/AskJeeoModal";
import NotificationsModal from "@/components/NotificationsModal";
import OnboardingTour from "@/components/OnboardingTour";
import { playChime } from "@/lib/chime";

const NAV = [
  { to: "/", label: "Today", icon: CalendarClock, id: "today" },
  { to: "/places", label: "Places", icon: Compass, id: "places" },
  { to: "/friends", label: "Friends", icon: Users, id: "friends" },
  { to: "/walk", label: "Walk", icon: Shield, id: "walk" },
  { to: "/rewards", label: "Rewards", icon: Trophy, id: "rewards" },
];

// Bundled emblems for built-in campuses (onboarded schools use their uploaded logo)
const SCHOOL_LOGOS = {
  tamu: "/tamu-emblem.png",
  ttu: "/ttu-emblem.png",
};

// Resolve a logo value to a usable <img src> (prefixes backend for /api asset paths)
// Auto-fit the header school name: short names bigger, long names smaller (always fully visible).
const schoolNameSize = (name) => {
  const len = (name || "Campus").length;
  if (len <= 12) return "15px";
  if (len <= 18) return "13px";
  if (len <= 26) return "11px";
  if (len <= 34) return "10px";
  return "9px";
};

const resolveLogo = (user) => {
  const raw = user?.school_logo || SCHOOL_LOGOS[user?.school];
  if (!raw) return null;
  return raw.startsWith("/api/") ? `${API}${raw}` : raw;
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [installEvt, setInstallEvt] = useState(null);
  const [ideasUnread, setIdeasUnread] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [bellPing, setBellPing] = useState(false);
  const prevNotif = useRef(null);
  const [askOpen, setAskOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const dark = useMode() === "dark";
  const logoSrc = resolveLogo(user);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = dark ? "#09090b" : "";
    return () => { document.body.style.backgroundColor = prev; };
  }, [dark]);

  const navItems = user?.is_admin
    ? [...NAV,
       { to: "/admin/schools", label: "Campuses", icon: Building2, id: "admin" },
       { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "analytics" }]
    : NAV;

  useEffect(() => {
    if (prevNotif.current !== null && notifCount > prevNotif.current) {
      setBellPing(true);
      playChime();
      const t = setTimeout(() => setBellPing(false), 1000);
      prevNotif.current = notifCount;
      return () => clearTimeout(t);
    }
    prevNotif.current = notifCount;
  }, [notifCount]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get("/ideas/notifications/unread-count")
      .then((r) => { if (alive) setIdeasUnread(r.data.unread || 0); }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    const onRead = () => setIdeasUnread(0);
    window.addEventListener("ideas-read", onRead);
    return () => { alive = false; clearInterval(id); window.removeEventListener("ideas-read", onRead); };
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchNotif = () => api.get("/notifications/feed")
      .then((r) => { if (alive) setNotifCount(r.data.count || 0); }).catch(() => {});
    fetchNotif();
    const id = setInterval(fetchNotif, 120000);
    return () => { alive = false; clearInterval(id); };
  }, [notifOpen]);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };

  return (
    <div className={`min-h-screen pb-20 xl:pb-0 ${dark ? "jeeo-dark" : ""}`}>
      {/* Top bar */}
      <header className="sticky top-0 z-[1000] backdrop-blur-xl bg-white/80 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0 shrink">
            <img src="/jeeo-logo-trans.png" alt="jeeo" className="h-12 w-auto shrink-0" />
            <span
              className="hidden sm:inline whitespace-nowrap mono tracking-[0.12em] uppercase text-muted-foreground border-l border-border pl-2 ml-1"
              style={{ fontSize: schoolNameSize(user?.school_name) }}
            >
              {user?.school_name || "Campus"}
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1 shrink-0">
            {navItems.map((n) => {
              const active = pathname === n.to;
              return (
                <Link
                  key={n.id}
                  to={n.to}
                  data-testid={`nav-${n.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    active ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                  }`}
                >
                  <span className="relative flex">
                    <n.icon size={16} strokeWidth={1.5} />
                    {n.id === "rewards" && ideasUnread > 0 && (
                      <span data-testid="ideas-unread-dot" className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                  </span> {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle"
              onClick={toggleMode}
              className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
              title={dark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {dark ? <Sun size={16} strokeWidth={1.7} /> : <Moon size={16} strokeWidth={1.7} />}
            </button>
            <button
              data-testid="notifications-bell"
              onClick={() => setNotifOpen(true)}
              className="relative w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
              title="Notifications"
            >
              <Bell size={16} strokeWidth={1.7} className={bellPing ? "bell-ring" : ""} />
              {notifCount > 0 && (
                <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[9px] font-bold flex items-center justify-center">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
            <button
              data-testid="ask-jeeo-btn"
              onClick={() => setAskOpen(true)}
              className="flex items-center gap-1.5 border border-brand text-brand rounded-md px-3 py-2 text-xs font-bold hover:bg-brand hover:text-white transition-colors"
              title="Ask jeeo what to do"
            >
              <Sparkles size={14} strokeWidth={2} /> <span className="hidden sm:inline">Ask jeeo</span>
            </button>
            {installEvt && (
              <button
                data-testid="install-app-btn"
                onClick={install}
                className="flex items-center gap-1.5 bg-brand text-white rounded-md px-3 py-2 text-xs font-bold hover:opacity-90 transition-colors"
              >
                <Download size={14} strokeWidth={2} /> <span className="hidden sm:inline">Install app</span>
              </button>
            )}
            {user?.founding_member && <FoundingBadge compact className="mr-0.5" />}
            {user?.innovator && <InnovatorBadge compact className="mr-0.5" />}
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-border object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-bold">
                {user?.name?.[0] || "A"}
              </div>
            )}
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 rounded-md hover:bg-secondary transition-colors"
              title="Log out"
            >
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto relative">
        <div className="sticky top-[64px] z-[900] h-0 flex justify-end pointer-events-none">
          <div
            data-testid="school-logo"
            title={user?.school_name || "Campus"}
            style={dark ? { backgroundColor: "#ffffff" } : undefined}
            className={`pointer-events-auto mt-4 mr-4 lg:mr-6 w-20 h-20 rounded-full flex items-center justify-center overflow-hidden ${dark ? "p-1.5 shadow-lg shadow-black/50" : ""}`}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={user?.school_name || "Campus logo"}
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="w-full h-full flex items-center justify-center rounded-full bg-brand text-white font-display font-semibold text-sm">
                {user?.school_short || "TAMU"}
              </span>
            )}
          </div>
        </div>
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="xl:hidden fixed bottom-0 inset-x-0 z-[1000] backdrop-blur-xl bg-white/90 border-t border-border">
        <div className="flex">
          {navItems.map((n) => {
            const active = pathname === n.to;
            return (
              <Link
                key={n.id}
                to={n.to}
                data-testid={`mnav-${n.id}`}
                className={`flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold ${
                  active ? "text-brand" : "text-muted-foreground"
                }`}
              >
                <span className="relative flex">
                  <n.icon size={20} strokeWidth={1.5} />
                  {n.id === "rewards" && ideasUnread > 0 && (
                    <span data-testid="mideas-unread-dot" className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </span> {n.label}
              </Link>
            );
          })}
        </div>
      </nav>

      <AskJeeoModal open={askOpen} onClose={() => setAskOpen(false)} />
      <NotificationsModal open={notifOpen} onClose={() => setNotifOpen(false)} />
      <OnboardingTour />
    </div>
  );
}
